import { CertifyTaskType } from 'components/pages/task/type'
import { UnknownUserID } from 'components/pages/user/user-display-name'
import { TaskValue } from 'lib/actions/task'
import { MetaSynchronousHeader } from 'lib/constants/platform'
import { UserContext } from 'lib/context/app'
import { useClientRPC } from 'lib/rpcts'
import { AccessReviewSearchServiceClient } from 'pbts/c1/api/accessreview/v1/accessreview_search.client'
import {
    BulkActionProgress,
    GetTaskActionStateRequest,
    TaskActionsServiceApproveRequest,
    TaskActionsServiceApproveWithStepUpRequest,
    TaskActionsServiceBulkRequest,
    TaskActionsServiceBulkResponse,
    TaskActionsServiceDenyRequest,
    TaskActionsServiceEscalateToEmergencyAccessRequest,
    TaskActionsServiceGetBulkActionProgressByIdRequest,
    TaskActionsServiceHardResetRequest,
    TaskActionsServiceMarkProvisionCompleteRequest,
    TaskActionsServiceMarkProvisionErroredRequest,
    TaskActionsServiceProcessNowRequest,
    TaskActionsServiceReassignRequest,
    TaskActionsServiceRestartRequest,
    TaskActionsServiceSkipStepRequest,
    TaskActionsServiceUpdateGrantDurationRequest,
    TaskActionsServiceUpdateRequestDataRequest,
    TaskActionStateItem,
} from 'pbts/c1/api/task/v1/actions'
import { TaskActionsServiceClient } from 'pbts/c1/api/task/v1/actions.client'
import { TaskSearchRequest } from 'pbts/c1/api/task/v1/search'
import { TaskActionState, TaskActionType } from 'pbts/c1/api/task/v1/task'
import { User } from 'pbts/c1/api/user/v1/user'
import { Duration as PbDuration } from 'pbts/google/protobuf/duration'
import { Struct } from 'pbts/google/protobuf/struct'
import { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { RollupCountManager } from 'redux_toolkit/lib/RollupCountManager'
import { setKeyValue } from 'redux_toolkit/slices/keyValueSlice'
import {
    approveTaskApi,
    approveWithStepUpTaskApi,
    bulkActionApi,
    denyTaskApi,
    escalateTaskApi,
    hardResetTaskApi,
    markProvisionCompleteApi,
    markProvisionErroredApi,
    processNowTaskApi,
    reassignTaskApi,
    restartTaskApi,
    skipStepTaskApi,
    updateGrantDurationTaskApi,
    updateRequestDataTaskApi,
} from 'redux_toolkit/tasks'

export type TicketActionTrackerVal = {
    userId: string
    appId: string
    appEntitlementId: string
    appUserId: string
    accessReviewId: string
    taskId: string
}

export type BulkActionTrackerVal = {
    userId?: string
    appId?: string
    appEntitlementId?: string
    appUserId?: string
    accessReviewId: string
    primaryEntityType?: 'user' | 'app' | 'appUser' | 'appEntitlement' // Track which entity type was used
    isPendingAction?: boolean // Track if this was a pending action for cache invalidation
}

const separator = '🌮'
export const appEntKey = (appId: string, entId: string): string => `${appId}${separator}${entId}`
export const aekDecoded = (aek: string): { appId: string; appEntitlementId: string } => {
    const [appId, appEntitlementId] = aek.split(separator)
    return { appId, appEntitlementId }
}

export class TicketActionTracker {
    private dispatch: ReturnType<typeof useDispatch>
    private taskActionServiceClient?: TaskActionsServiceClient
    private isPolling = false
    private isBulkActionsPolling = false
    private reviewerUserId: string
    private trackerTicketActions: Record<string, TicketActionTrackerVal> = {}
    private trackerBulkActions: Record<string, BulkActionTrackerVal> = {}
    private rollupCountManager: RollupCountManager

    private escalateTask: ReturnType<typeof escalateTaskApi>['mutate']
    private restartTask: ReturnType<typeof restartTaskApi>['mutate']
    private hardResetTask: ReturnType<typeof hardResetTaskApi>['mutate']
    private processNowTask: ReturnType<typeof processNowTaskApi>['mutate']
    private approveTask: ReturnType<typeof approveTaskApi>['mutate']
    private approveWithStepUpTask: ReturnType<typeof approveWithStepUpTaskApi>['mutate']
    private denyTask: ReturnType<typeof denyTaskApi>['mutate']
    private reassignTask: ReturnType<typeof reassignTaskApi>['mutate']
    private bulkAction: ReturnType<typeof bulkActionApi>['mutate']
    private skipStepTask: ReturnType<typeof skipStepTaskApi>['mutate']
    private updateRequestDataTask: ReturnType<typeof updateRequestDataTaskApi>['mutate']
    private updateGrantDurationTask: ReturnType<typeof updateGrantDurationTaskApi>['mutate']

    private markProvisionCompleteTask: ReturnType<typeof markProvisionCompleteApi>['mutate']
    private markProvisionErroredTask: ReturnType<typeof markProvisionErroredApi>['mutate']

    constructor(
        dispatch: ReturnType<typeof useDispatch>,
        taskActionServiceClient: TaskActionsServiceClient,
        reviewerUserId: string,
        rollupCountManager: RollupCountManager,
        escalateTask: ReturnType<typeof escalateTaskApi>['mutate'],
        restartTask: ReturnType<typeof restartTaskApi>['mutate'],
        approveTask: ReturnType<typeof approveTaskApi>['mutate'],
        approveWithStepUpTask: ReturnType<typeof approveWithStepUpTaskApi>['mutate'],
        denyTask: ReturnType<typeof denyTaskApi>['mutate'],
        reassignTask: ReturnType<typeof reassignTaskApi>['mutate'],
        bulkAction: ReturnType<typeof bulkActionApi>['mutate'],
        markProvisionCompleteTask: ReturnType<typeof markProvisionCompleteApi>['mutate'],
        markProvisionErroredTask: ReturnType<typeof markProvisionErroredApi>['mutate'],
        hardResetTask: ReturnType<typeof hardResetTaskApi>['mutate'],
        processNowTask: ReturnType<typeof processNowTaskApi>['mutate'],
        skipStepTask: ReturnType<typeof skipStepTaskApi>['mutate'],
        updateRequestDataTask: ReturnType<typeof updateRequestDataTaskApi>['mutate'],
        updateGrantDurationTask: ReturnType<typeof updateGrantDurationTaskApi>['mutate'],
    ) {
        this.dispatch = dispatch
        this.taskActionServiceClient = taskActionServiceClient
        this.reviewerUserId = reviewerUserId
        this.rollupCountManager = rollupCountManager
        this.escalateTask = escalateTask
        this.restartTask = restartTask
        this.approveTask = approveTask
        this.approveWithStepUpTask = approveWithStepUpTask
        this.denyTask = denyTask
        this.reassignTask = reassignTask
        this.bulkAction = bulkAction
        this.markProvisionCompleteTask = markProvisionCompleteTask
        this.markProvisionErroredTask = markProvisionErroredTask
        this.hardResetTask = hardResetTask
        this.processNowTask = processNowTask
        this.skipStepTask = skipStepTask
        this.updateRequestDataTask = updateRequestDataTask
        this.updateGrantDurationTask = updateGrantDurationTask

        setInterval(this.pollTicketActions, 500)
        setInterval(this.pollBulkActions, 2000)
    }

    private pollTicketActions = async (): Promise<void> => {
        if (this.isPolling) {
            return
        }

        this.isPolling = true
        const ticketActionIds = Object.keys(this.trackerTicketActions)
        if (ticketActionIds.length === 0) {
            this.isPolling = false
            return
        }

        let ticketActionStatesFromApi: Array<TaskActionStateItem> | undefined
        try {
            const resp = await this.taskActionServiceClient?.getTaskActionState(
                GetTaskActionStateRequest.create({
                    ticketActionIds,
                }),
            )
            ticketActionStatesFromApi = resp?.response?.items
            if (!ticketActionStatesFromApi) {
                this.isPolling = false
                return
            }
        } catch (e) {
            this.isPolling = false
            return
        }

        for (const tApi of ticketActionStatesFromApi) {
            if (tApi.state === TaskActionState.DONE || tApi.state === TaskActionState.ERROR) {
                const ticketActionId = tApi.ticketActionId
                const trackerAction = this.trackerTicketActions[ticketActionId]
                if (!trackerAction) {
                    continue
                }
                const { userId, accessReviewId, appId, appEntitlementId, appUserId, taskId } = trackerAction
                if (!userId || !accessReviewId) {
                    continue
                }

                // Clear tracking state now that action is complete
                this.dispatch(setKeyValue({ key: `tracking-${taskId}`, value: undefined }))

                // Remove from tracker and untrack from rollup manager
                delete this.trackerTicketActions[ticketActionId]
                this.rollupCountManager.untrackAction(accessReviewId, 'user', { userId }, ticketActionId)
                this.rollupCountManager.untrackAction(accessReviewId, 'app', { appId }, ticketActionId)
                this.rollupCountManager.untrackAction(accessReviewId, 'appUser', { appId, appUserId }, ticketActionId)
                this.rollupCountManager.untrackAction(accessReviewId, 'appEntitlement', { appId, appEntitlementId }, ticketActionId)
            }
        }

        this.isPolling = false
    }

    private pollBulkActions = async (): Promise<void> => {
        if (this.isBulkActionsPolling) {
            return
        }

        this.isBulkActionsPolling = true
        const bulkActionIds = Object.keys(this.trackerBulkActions)
        if (bulkActionIds.length === 0) {
            this.isBulkActionsPolling = false
            return
        }

        let progressList: Array<BulkActionProgress> | undefined
        try {
            const resp = await this.taskActionServiceClient?.getBulkActionProgressById(
                TaskActionsServiceGetBulkActionProgressByIdRequest.create({
                    bulkActionIds,
                }),
            )
            progressList = resp?.response.list
            if (!progressList) {
                this.isBulkActionsPolling = false
                return
            }
        } catch (e) {
            this.isBulkActionsPolling = false
            return
        }

        for (const progress of progressList) {
            const bulkActionId = progress.id
            const trackerBulkAction = this.trackerBulkActions[bulkActionId]
            if (!trackerBulkAction) {
                continue
            }
            const {
                userId,
                appId,
                appEntitlementId,
                accessReviewId,
                appUserId,
            } = trackerBulkAction
            if (
                // totalActions isn't populated when the item is created
                progress.totalActions === 0 ||
                progress.successActions + progress.errorActions !== progress.totalActions
            ) {
                continue
            }

            delete this.trackerBulkActions[bulkActionId]

            // Untrack ALL entities and trigger debounced refresh for each
            // This matches the behavior of individual task actions
            if (userId) {
                this.rollupCountManager.untrackBulkAction(accessReviewId, 'user', { userId }, bulkActionId)
            }
            if (appId) {
                this.rollupCountManager.untrackBulkAction(accessReviewId, 'app', { appId }, bulkActionId)
            }
            if (appId && appUserId) {
                this.rollupCountManager.untrackBulkAction(accessReviewId, 'appUser', { appId, appUserId }, bulkActionId)
            }
            if (appId && appEntitlementId) {
                this.rollupCountManager.untrackBulkAction(accessReviewId, 'appEntitlement', { appId, appEntitlementId }, bulkActionId)
            }
        }

        this.isBulkActionsPolling = false
    }

    private applyOptimisticUpdates = (
        task: TaskValue,
        ticketAction: 'approve' | 'deny',
    ): void => {
        const { appUser, accessReview, id: ticketId, appEntitlements } = task

        const userId = appUser?.identityUserId || UnknownUserID
        const appUserId = appUser?.id
        const appId = appUser?.appId
        const accessReviewId = accessReview?.id
        const appEntitlementId = appEntitlements?.[0]?.id

        if (!appUserId || !appId || !accessReviewId || !ticketId || !appEntitlementId) {
            return
        }

        // Set optimistic UI state (button shows "Certified"/"Denied")
        this.dispatch(setKeyValue({ key: `optimistic-${ticketId}`, value: ticketAction }))

        // Optimistically update counts
        this.rollupCountManager.updateCountsOptimistically('user', { userId })
        this.rollupCountManager.updateCountsOptimistically('app', { appId })
        this.rollupCountManager.updateCountsOptimistically('appUser', { appId, appUserId })
        this.rollupCountManager.updateCountsOptimistically('appEntitlement', { appId, appEntitlementId })
    }

    private revertOptimisticUpdates = (task: TaskValue): void => {
        const { id: ticketId } = task
        // Clear the optimistic UI state and tracking state
        this.dispatch(setKeyValue({ key: `optimistic-${ticketId}`, value: undefined }))
        this.dispatch(setKeyValue({ key: `tracking-${ticketId}`, value: undefined }))
        // Note: We don't revert counts because they'll be refreshed by the backend via notify
    }

    private revertBulkOptimisticUpdates = (
        accessReviewId: string,
        entities: { userId?: string; appId?: string; appEntitlementId?: string; appUserId?: string },
    ): void => {
        // Trigger immediate refresh to get actual counts from backend
        const { userId, appId, appEntitlementId, appUserId } = entities

        if (appId && appEntitlementId) {
            void this.rollupCountManager.immediateRefresh(accessReviewId, 'appEntitlement', { appId, appEntitlementId })
        } else if (appId && appUserId) {
            void this.rollupCountManager.immediateRefresh(accessReviewId, 'appUser', { appId, appUserId })
        } else if (appId) {
            void this.rollupCountManager.immediateRefresh(accessReviewId, 'app', { appId })
        } else if (userId) {
            void this.rollupCountManager.immediateRefresh(accessReviewId, 'user', { userId })
        }
    }

    private trackTicketActionForPolling = (task: TaskValue, ticketActionId: string, clearOptimistic = false): void => {
        const { appUser, accessReview, id: ticketId, appEntitlements } = task

        const userId = appUser?.identityUserId || UnknownUserID
        const appUserId = appUser?.id
        const appId = appUser?.appId
        const accessReviewId = accessReview?.id
        const appEntitlementId = appEntitlements?.[0]?.id

        if (!appUserId || !appId || !accessReviewId || !ticketId || !appEntitlementId) {
            return
        }

        // Clear optimistic state if needed (e.g., for restart/hardReset)
        if (clearOptimistic) {
            this.dispatch(setKeyValue({ key: `optimistic-${ticketId}`, value: undefined }))
        }

        // Set tracking state to skip "Submitted" status
        this.dispatch(setKeyValue({ key: `tracking-${ticketId}`, value: 'tracking' }))

        // Add this ticket action to the tracker for completion polling
        this.trackerTicketActions[ticketActionId] = {
            userId,
            appId,
            appEntitlementId,
            appUserId,
            accessReviewId,
            taskId: ticketId,
        }

        // Track actions with rollup count manager for polling
        this.rollupCountManager.trackAction(accessReviewId, 'user', { userId }, ticketActionId)
        this.rollupCountManager.trackAction(accessReviewId, 'app', { appId }, ticketActionId)
        this.rollupCountManager.trackAction(accessReviewId, 'appUser', { appId, appUserId }, ticketActionId)
        this.rollupCountManager.trackAction(accessReviewId, 'appEntitlement', { appId, appEntitlementId }, ticketActionId)
    }

    private trackTicketAction = (
        task: TaskValue,
        ticketAction: 'approve' | 'deny' | 'restart' | 'reassign' | 'escalateToEmergencyAccess' | 'markProvisionComplete' | 'markProvisionErrored' | 'updateRequestData' | 'updateGrantDuration',
        ticketActionId: string,
    ): void => {
        // For approve/deny, apply optimistic updates
        if (ticketAction === 'approve' || ticketAction === 'deny') {
            this.applyOptimisticUpdates(task, ticketAction)
        }

        // For restart, clear optimistic state (task is being reopened)
        const shouldClearOptimistic = ticketAction === 'restart'

        // Track for polling
        this.trackTicketActionForPolling(task, ticketActionId, shouldClearOptimistic)
    }

    public approve = async (task: TaskValue, comment?: string): Promise<TaskValue> => {
        const { id, policy } = task

        // Apply optimistic updates IMMEDIATELY before API call
        this.applyOptimisticUpdates(task, 'approve')

        try {
            const { data, error } = await this.approveTask(
                TaskActionsServiceApproveRequest.create({
                    taskId: id,
                    comment,
                    policyStepId: policy?.current?.id,
                    expandMask: { paths: ['*'] },
                }),
                MetaSynchronousHeader,
            )

            if (error) {
                // Revert optimistic updates on error
                this.revertOptimisticUpdates(task)
                throw error
            }

            if (!data) {
                // Revert optimistic updates on error
                this.revertOptimisticUpdates(task)
                throw new Error('No response from approving task!')
            }

            // Track the action for completion polling
            if (!data.ticketActionId) {
                this.revertOptimisticUpdates(task)
                throw new Error('Missing ticketActionId from approve response')
            }

            this.trackTicketActionForPolling(task, data.ticketActionId)

            return data
        } catch (err) {
            // Revert optimistic updates on any error
            this.revertOptimisticUpdates(task)
            throw err
        }
    }

    public approveWithStepUp = async (task: TaskValue, comment?: string, stepUpTransactionId?: string): Promise<TaskValue> => {
        const { id, policy } = task

        // Apply optimistic updates IMMEDIATELY before API call
        this.applyOptimisticUpdates(task, 'approve')

        try {
            const { data, error } = await this.approveWithStepUpTask(
                TaskActionsServiceApproveWithStepUpRequest.create({
                    taskId: id,
                    comment,
                    policyStepId: policy?.current?.id,
                    stepUpTransactionId,
                    expandMask: { paths: ['*'] },
                }),
                MetaSynchronousHeader,
            )

            if (error) {
                // Revert optimistic updates on error
                this.revertOptimisticUpdates(task)
                throw error
            }

            if (!data) {
                // Revert optimistic updates on error
                this.revertOptimisticUpdates(task)
                throw new Error('No response from approving task with step-up!')
            }

            if (data.stepUpRedirectUrl) {
                window.location.href = data.stepUpRedirectUrl
                return data
            }

            // Track the action for completion polling
            if (!data.ticketActionId) {
                this.revertOptimisticUpdates(task)
                throw new Error('Missing ticketActionId from approveWithStepUp response')
            }

            this.trackTicketActionForPolling(task, data.ticketActionId)

            return data
        } catch (err) {
            // Revert optimistic updates on any error
            this.revertOptimisticUpdates(task)
            throw err
        }
    }

    public deny = async (task: TaskValue, comment?: string): Promise<TaskValue> => {
        const { id, policy } = task

        // Apply optimistic updates IMMEDIATELY before API call
        this.applyOptimisticUpdates(task, 'deny')

        try {
            const { data, error } = await this.denyTask(
                TaskActionsServiceDenyRequest.create({
                    taskId: id,
                    comment,
                    policyStepId: policy?.current?.id,
                    expandMask: { paths: ['*'] },
                }),
                MetaSynchronousHeader,
            )

            if (error) {
                // Revert optimistic updates on error
                this.revertOptimisticUpdates(task)
                throw error
            }

            if (!data) {
                // Revert optimistic updates on error
                this.revertOptimisticUpdates(task)
                throw new Error('No response from denying task!')
            }

            // Track the action for completion polling
            if (!data.ticketActionId) {
                this.revertOptimisticUpdates(task)
                throw new Error('Missing ticketActionId from deny response')
            }

            this.trackTicketActionForPolling(task, data.ticketActionId)

            return data
        } catch (err) {
            // Revert optimistic updates on any error
            this.revertOptimisticUpdates(task)
            throw err
        }
    }

    public escalate = async (task: TaskValue): Promise<TaskValue> => {
        const { id, policy } = task

        const { data, error } = await this.escalateTask(
            TaskActionsServiceEscalateToEmergencyAccessRequest.create({
                taskId: id,
                comment: '',
                policyStepId: policy?.current?.id || '',
                expandMask: { paths: ['*'] },
            }),
            MetaSynchronousHeader,
        )

        if (error) {
            throw error
        }

        if (!data) {
            throw new Error('No response from escalating task!')
        }

        // The expander should set the ticketActionId from the response
        if (!data.ticketActionId) {
            throw new Error('No ticket action ID from escalating task!')
        }

        this.trackTicketAction(task, 'escalateToEmergencyAccess', data.ticketActionId)

        return data
    }

    public restart = async (task: TaskValue): Promise<TaskValue> => {
        const { id, appUser, accessReview, appEntitlements } = task

        const { data, error } = await this.restartTask(
            TaskActionsServiceRestartRequest.create({
                taskId: id,
                comment: '',
                expandMask: { paths: ['*'] },
            }),
            MetaSynchronousHeader,
        )

        if (error) {
            throw error
        }

        if (!data) {
            throw new Error('No response from restarting task!')
        }

        // The expander should set the ticketActionId from the response
        if (!data.ticketActionId) {
            throw new Error('No ticket action ID from restarting task!')
        }

        this.trackTicketAction(task, 'restart', data.ticketActionId)

        // Restart reopens tasks, so immediately refresh counts to reflect new open tasks
        // Only refresh the most specific entity type to avoid 4x API calls
        const userId = appUser?.identityUserId
        const appUserId = appUser?.id
        const appId = appUser?.appId
        const accessReviewId = accessReview?.id
        const appEntitlementId = appEntitlements?.[0]?.id

        if (accessReviewId) {
            // Refresh most specific entity only (same pattern as bulk actions)
            if (appId && appEntitlementId) {
                this.rollupCountManager.immediateRefresh(accessReviewId, 'appEntitlement', { appId, appEntitlementId })
            } else if (appId && appUserId) {
                this.rollupCountManager.immediateRefresh(accessReviewId, 'appUser', { appId, appUserId })
            } else if (appId) {
                this.rollupCountManager.immediateRefresh(accessReviewId, 'app', { appId })
            } else if (userId) {
                this.rollupCountManager.immediateRefresh(accessReviewId, 'user', { userId })
            }
        }

        return data
    }

    public hardReset = async (task: TaskValue): Promise<TaskValue> => {
        const { id, appUser, accessReview, appEntitlements } = task

        const { data, error } = await this.hardResetTask(
            TaskActionsServiceHardResetRequest.create({
                taskId: id,
                comment: '',
                expandMask: { paths: ['*'] },
            }),
            MetaSynchronousHeader,
        )

        if (error) {
            throw error
        }

        if (!data) {
            throw new Error('No response from hard resetting task!')
        }

        // The expander should set the ticketActionId from the response
        if (!data.ticketActionId) {
            throw new Error('No ticket action ID from hard resetting task!')
        }

        this.trackTicketAction(task, 'restart', data.ticketActionId)

        // Hard reset reopens tasks, so immediately refresh counts to reflect new open tasks
        // Only refresh the most specific entity type to avoid 4x API calls
        const userId = appUser?.identityUserId
        const appUserId = appUser?.id
        const appId = appUser?.appId
        const accessReviewId = accessReview?.id
        const appEntitlementId = appEntitlements?.[0]?.id

        if (accessReviewId) {
            // Refresh most specific entity only (same pattern as bulk actions)
            if (appId && appEntitlementId) {
                this.rollupCountManager.immediateRefresh(accessReviewId, 'appEntitlement', { appId, appEntitlementId })
            } else if (appId && appUserId) {
                this.rollupCountManager.immediateRefresh(accessReviewId, 'appUser', { appId, appUserId })
            } else if (appId) {
                this.rollupCountManager.immediateRefresh(accessReviewId, 'app', { appId })
            } else if (userId) {
                this.rollupCountManager.immediateRefresh(accessReviewId, 'user', { userId })
            }
        }

        return data
    }

    public processNow = async (task: TaskValue): Promise<TaskValue> => {
        const { id } = task

        const { data, error } = await this.processNowTask(
            TaskActionsServiceProcessNowRequest.create({
                taskId: id,
                expandMask: { paths: ['*'] },
            }),
            MetaSynchronousHeader,
        )

        if (error) {
            throw error
        }

        if (!data) {
            throw new Error('No response from processing now!')
        }

        return data
    }

    public reassign = async (
        task: TaskValue,
        users: Array<User>,
        comment?: string,
    ): Promise<TaskValue> => {
        const { id, policy } = task

        const { data, error } = await this.reassignTask(
            TaskActionsServiceReassignRequest.create({
                taskId: id,
                newStepUserIds: users.map((u) => u.id),
                comment,
                policyStepId: policy?.current?.id,
                expandMask: { paths: ['*'] },
            }),
            MetaSynchronousHeader,
        )

        if (error) {
            throw error
        }

        if (!data) {
            throw new Error('No response from reassigning task!')
        }

        // The expander should set the ticketActionId from the response
        if (!data.ticketActionId) {
            throw new Error('No ticket action ID from reassigning task!')
        }

        this.trackTicketAction(task, 'reassign', data.ticketActionId)

        return data
    }

    public updateRequestData = async (
        task: TaskValue,
        requestData: Struct,
    ): Promise<TaskValue> => {
        const { id } = task

        const { data, error } = await this.updateRequestDataTask(
            TaskActionsServiceUpdateRequestDataRequest.create({
                taskId: id,
                data: requestData,
                expandMask: { paths: ['*'] },
            }),
            MetaSynchronousHeader,
        )

        if (error) {
            throw error
        }

        if (!data) {
            throw new Error('No response from updating request data on task!')
        }

        if (!data.ticketActionId) {
            throw new Error('No ticket action ID from updating request data on task!')
        }

        this.trackTicketAction(task, 'updateRequestData', data.ticketActionId)

        return data
    }

    public updateGrantDuration = async (
        task: TaskValue,
        duration: PbDuration,
    ): Promise<TaskValue> => {
        const { id } = task

        const { data, error } = await this.updateGrantDurationTask(
            TaskActionsServiceUpdateGrantDurationRequest.create({
                taskId: id,
                duration,
                expandMask: { paths: ['*'] },
            }),
            MetaSynchronousHeader,
        )

        if (error) {
            throw error
        }

        if (!data) {
            throw new Error('No response from updating grant duration on task!')
        }

        if (!data.ticketActionId) {
            throw new Error('No ticket action ID from updating grant duration on task!')
        }

        this.trackTicketAction(task, 'updateGrantDuration', data.ticketActionId)

        return data
    }

    public markProvisionComplete = async (
        task: TaskValue,
        comment?: string,
    ): Promise<TaskValue> => {
        const { id, policy } = task

        const { data, error } = await this.markProvisionCompleteTask(
            TaskActionsServiceMarkProvisionCompleteRequest.create({
                taskId: id,
                comment,
                policyStepId: policy?.current?.id,
                expandMask: { paths: ['*'] },
            }),
            MetaSynchronousHeader,
        )

        if (error) {
            throw error
        }

        if (!data) {
            throw new Error('No response from marking provision complete on task!')
        }

        // The expander should set the ticketActionId from the response
        if (!data.ticketActionId) {
            throw new Error('No ticket action ID from marking provision complete on task!')
        }

        this.trackTicketAction(task, 'markProvisionComplete', data.ticketActionId)

        return data
    }

    public markProvisionErrored = async (
        task: TaskValue,
        comment?: string,
    ): Promise<TaskValue> => {
        const { id, policy } = task

        const { data, error } = await this.markProvisionErroredTask(
            TaskActionsServiceMarkProvisionErroredRequest.create({
                taskId: id,
                comment,
                policyStepId: policy?.current?.id,
                expandMask: { paths: ['*'] },
            }),
            MetaSynchronousHeader,
        )

        if (error) {
            throw error
        }

        if (!data) {
            throw new Error('No response from marking provision errored on task!')
        }

        // The expander should set the ticketActionId from the response
        if (!data.ticketActionId) {
            throw new Error('No ticket action ID from marking provision errored on task!')
        }

        this.trackTicketAction(task, 'markProvisionErrored', data.ticketActionId)

        return data
    }

    public skipStep = async (task: TaskValue): Promise<TaskValue> => {
        const { id, policy } = task

        const { data, error } = await this.skipStepTask(
            TaskActionsServiceSkipStepRequest.create({
                taskId: id,
                policyStepId: policy?.current?.id,
                expandMask: { paths: ['*'] },
            }),
            MetaSynchronousHeader,
        )

        if (error) {
            throw error
        }

        if (!data) {
            throw new Error('No response from skip step!')
        }

        return data
    }

    public performBulkActionByUser = async (
        userId: string,
        accessReviewId: string,
        actionType: TaskActionType,
        appUserIds?: Array<string>,
        comment?: string,
        actionArgs?: TaskActionsServiceBulkRequest['actionArgs'],
    ): Promise<TaskActionsServiceBulkResponse> => {
        const { data, error } = await this.bulkAction(
            TaskActionsServiceBulkRequest.create({
                actionType,
                comment,
                actionArgs: actionArgs || undefined,
                searchRequest: TaskSearchRequest.create({
                    accessReviewIds: [accessReviewId],
                    assigneesInIds: [this.reviewerUserId],
                    accountOwnerIds: userId !== UnknownUserID ? [userId] : undefined,
                    appUserSubjectIds: appUserIds,
                    taskTypes: [CertifyTaskType],
                }),
            }),
            MetaSynchronousHeader,
        )

        if (error) {
            throw error
        }

        if (!data) {
            throw new Error('No response from bulk action task!')
        }

        this.trackerBulkActions[data.bulkActionId] = {
            userId,
            accessReviewId,
            primaryEntityType: 'user',
        }

        this.rollupCountManager.trackBulkAction(data.bulkActionId, 'user', { userId })

        return data
    }

    public performBulkActionForAppEntitlement = async (
        appId: string,
        appEntitlementId: string,
        accessReviewId: string,
        actionType: TaskActionType,
        comment?: string,
    ): Promise<TaskActionsServiceBulkResponse> => {
        const { data, error } = await this.bulkAction(
            TaskActionsServiceBulkRequest.create({
                actionType,
                comment,
                searchRequest: TaskSearchRequest.create({
                    accessReviewIds: [accessReviewId],
                    assigneesInIds: [this.reviewerUserId],
                    applicationIds: [appId],
                    appEntitlementIds: [appEntitlementId],
                    taskTypes: [CertifyTaskType],
                }),
            }),
            MetaSynchronousHeader,
        )

        if (error) {
            throw error
        }

        if (!data) {
            throw new Error('No response from bulk action task!')
        }

        this.trackerBulkActions[data.bulkActionId] = {
            appId,
            appEntitlementId,
            accessReviewId,
            primaryEntityType: 'appEntitlement',
        }
        this.rollupCountManager.trackBulkAction(data.bulkActionId, 'appEntitlement', { appId, appEntitlementId })

        return data
    }

    public performGenericBulkAction = async (
        request: TaskActionsServiceBulkRequest,
        expectedTaskCount?: number,
    ): Promise<TaskActionsServiceBulkResponse> => {
        // Extract entity IDs before API call for optimistic updates
        const userId = request.searchRequest?.accountOwnerIds?.[0] || ''
        const appId = request.searchRequest?.applicationIds?.[0] || ''
        const appEntitlementId = request.searchRequest?.appEntitlementIds?.[0] || ''
        const appUserId = request.searchRequest?.appUserSubjectIds?.[0] || ''
        const accessReviewId = request.searchRequest?.accessReviewIds?.[0] || ''

        // Apply optimistic updates for APPROVE/DENY actions
        const isApproveOrDeny = request.actionType === TaskActionType.APPROVE ||
            request.actionType === TaskActionType.DENY

        if (isApproveOrDeny && expectedTaskCount !== undefined && expectedTaskCount > 0) {
            // Update the most specific entity (priority: appEntitlement > appUser > app > user)
            if (appId && appEntitlementId) {
                this.rollupCountManager.updateCountsByAmount('appEntitlement', { appId, appEntitlementId }, expectedTaskCount)
            } else if (appId && appUserId) {
                this.rollupCountManager.updateCountsByAmount('appUser', { appId, appUserId }, expectedTaskCount)
            } else if (appId) {
                this.rollupCountManager.updateCountsByAmount('app', { appId }, expectedTaskCount)
            } else if (userId) {
                this.rollupCountManager.updateCountsByAmount('user', { userId }, expectedTaskCount)
            }
        }

        try {
            const { data, error } = await this.bulkAction(request, MetaSynchronousHeader)

            if (error) {
                // Revert optimistic updates on error
                if (isApproveOrDeny && expectedTaskCount !== undefined && expectedTaskCount > 0) {
                    this.revertBulkOptimisticUpdates(accessReviewId, { userId, appId, appEntitlementId, appUserId })
                }
                throw error
            }
            if (!data) {
                throw new Error('No response from bulk action task!')
            }

            // Track ALL entity types for loading state & disabled buttons
            if (userId) {
                this.rollupCountManager.trackBulkAction(data.bulkActionId, 'user', { userId })
            }
            if (appId) {
                this.rollupCountManager.trackBulkAction(data.bulkActionId, 'app', { appId })
            }
            if (appId && appUserId) {
                this.rollupCountManager.trackBulkAction(data.bulkActionId, 'appUser', { appId, appUserId })
            }
            if (appId && appEntitlementId) {
                this.rollupCountManager.trackBulkAction(data.bulkActionId, 'appEntitlement', { appId, appEntitlementId })
            }

            // Determine primary entity based on most specific filter for tracking purposes
            // Note: All tracked entities will be refreshed on completion (see pollBulkActions)
            let primaryEntityType: 'user' | 'app' | 'appUser' | 'appEntitlement' | undefined
            if (appId && appEntitlementId) {
                primaryEntityType = 'appEntitlement'
            } else if (appId && appUserId) {
                primaryEntityType = 'appUser'
            } else if (appId) {
                primaryEntityType = 'app'
            } else if (userId) {
                primaryEntityType = 'user'
            }

            this.trackerBulkActions[data.bulkActionId] = {
                accessReviewId,
                userId,
                appId,
                appEntitlementId,
                appUserId,
                primaryEntityType,
            }

            return data
        } catch (err) {
            // Ensure revert on any error
            if (isApproveOrDeny && expectedTaskCount !== undefined && expectedTaskCount > 0) {
                this.revertBulkOptimisticUpdates(accessReviewId, { userId, appId, appEntitlementId, appUserId })
            }
            throw err
        }
    }

    public clear = (): void => {
        this.rollupCountManager.clearCounts()
    }

    public fetchAppEntitlementCounts = async (accessReviewId: string, aeks: string[]): Promise<void> => {
        if (aeks.length === 0) return

        // Batch fetch all entitlement counts in a single request
        const entities = aeks.map((aek) => {
            const { appId, appEntitlementId } = aekDecoded(aek)
            return { appId, appEntitlementId }
        })

        await this.rollupCountManager.fetchBatchCounts(accessReviewId, 'appEntitlement', entities)
    }

    public fetchAppUserCounts = async (accessReviewId: string, appUserKeys: string[]): Promise<void> => {
        if (appUserKeys.length === 0) return

        // Batch fetch all app user counts in a single request
        const entities = appUserKeys
            .map((key) => {
                const [appId, appUserId] = key.split(':')
                if (appId && appUserId) {
                    return { appId, appUserId }
                }
                return null
            })
            .filter((e): e is { appId: string; appUserId: string } => e !== null)

        await this.rollupCountManager.fetchBatchCounts(accessReviewId, 'appUser', entities)
    }
}

let instance: TicketActionTracker | null = null

export const useTicketActionTracker = (): TicketActionTracker | null => {
    const userContext = useContext(UserContext)
    const dispatch = useDispatch()
    const taskActionServiceClient = useClientRPC(TaskActionsServiceClient)
    const accessReviewSearchServiceClient = useClientRPC(AccessReviewSearchServiceClient)
    const trackerRef = useRef<TicketActionTracker | null>(instance)
    const { mutate: escalateTask } = escalateTaskApi()
    const { mutate: restartTask } = restartTaskApi()
    const { mutate: hardResetTask } = hardResetTaskApi()
    const { mutate: processNowTask } = processNowTaskApi()
    const { mutate: approveTask } = approveTaskApi()
    const { mutate: approveWithStepUpTask } = approveWithStepUpTaskApi()
    const { mutate: denyTask } = denyTaskApi()
    const { mutate: reassignTask } = reassignTaskApi()
    const { mutate: bulkAction } = bulkActionApi()
    const { mutate: markProvisionCompleteTask } = markProvisionCompleteApi()
    const { mutate: markProvisionErroredTask } = markProvisionErroredApi()
    const { mutate: skipStepTask } = skipStepTaskApi()
    const { mutate: updateRequestDataTask } = updateRequestDataTaskApi()
    const { mutate: updateGrantDurationTask } = updateGrantDurationTaskApi()
    const [isReady, setIsReady] = useState(false)

    useEffect(
        () => {
            if (instance) {
                trackerRef.current = instance
                setIsReady(true)
                return
            }

            if (!taskActionServiceClient || !accessReviewSearchServiceClient || !userContext.user.id) {
                return
            }

            const rollupCountManager = new RollupCountManager(
                dispatch,
                accessReviewSearchServiceClient,
                userContext.user.id,
            )

            trackerRef.current = instance = new TicketActionTracker(
                dispatch,
                taskActionServiceClient,
                userContext.user.id,
                rollupCountManager,
                escalateTask,
                restartTask,
                approveTask,
                approveWithStepUpTask,
                denyTask,
                reassignTask,
                bulkAction,
                markProvisionCompleteTask,
                markProvisionErroredTask,
                hardResetTask,
                processNowTask,
                skipStepTask,
                updateRequestDataTask,
                updateGrantDurationTask,
            )
            setIsReady(true)
        },
        [
            taskActionServiceClient,
            userContext.user.id,
            dispatch,
            escalateTask,
            restartTask,
            approveTask,
            approveWithStepUpTask,
            denyTask,
            reassignTask,
            bulkAction,
            markProvisionCompleteTask,
            markProvisionErroredTask,
            accessReviewSearchServiceClient,
            hardResetTask,
            processNowTask,
            skipStepTask,
            updateRequestDataTask,
            updateGrantDurationTask,
        ],
    )

    if (!isReady) {
        return null
    }

    return trackerRef.current
}
